import {
	Alert,
	Badge,
	Box,
	Button,
	Col,
	Divider,
	Dropdown,
	Grid,
	Heading,
	Icons,
	Inline,
	RelativeTime,
	Select,
	Spinner,
	Stack,
	TabContent,
	Table,
	Tabs,
	TabsList,
	Text,
	Time,
} from "@sembark-travel/ui-base"
import { NavLink } from "@sembark-travel/ui-router"
import {
	CopyToClipboard,
	CopyToClipboardButton,
} from "@sembark-travel/ui-copy-to-clipboard"
import {
	arrayMutators,
	CheckboxInputField,
	FieldArray,
	Form,
	GetFieldValue,
	RadioInputField,
	SubmissionError,
	TextInputField,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import {
	ListView,
	Search,
	TSearchParams,
	areAdvancedFiltersAppliedDefault,
	useSearch,
} from "@sembark-travel/ui-list"
import { DateRangePickerField, SelectField } from "@sembark-travel/ui-form"
import { dateToUTCString, endOf, startOf } from "@sembark-travel/datetime-utils"
import { useXHR } from "@sembark-travel/xhr"
import { type ReactNode, useState } from "react"
import * as Validator from "yup"
import { IUser } from "../Auth"
import { generatePath } from "../router-utils"
import { SelectUsers } from "../Users"
import { PlatformIcon } from "./PlatformIcon"
import {
	TIntegration,
	TNewOAuthIntegrationSchema,
	TOAuthAccessLog,
	TOAuthAccessLogOutcome,
	TOAuthClientCredentials,
	TOAuthClientType,
	TOAuthIntegration,
	TOAuthIntegrationOptions,
	TOAuthScope,
	XHR,
	useFetchConnectedOAuthApps,
	useFetchIntegration,
	useFetchOAuthIntegrationOptions,
} from "./store"

const CUSTOM_CLIENT_TYPE: TOAuthClientType = "custom"

const DEFAULT_ALLOWED_SCOPES = ["trips:read"]

/**
 * Only the shape is checked here; https versus loopback http depends on the
 * API's environment, so the server decides that and reports it per field.
 */
function isAbsoluteHttpUrl(value: string | undefined): boolean {
	if (!value) return false
	try {
		const url = new URL(value)
		return url.protocol === "https:" || url.protocol === "http:"
	} catch {
		return false
	}
}

const redirectUriValidator = Validator.string()
	.required("Please provide the redirect URI")
	.test(
		"absolute-http-url",
		"Please provide an absolute http(s) URL",
		isAbsoluteHttpUrl
	)

const newOAuthIntegrationValidator = Validator.object().shape({
	name: Validator.string()
		.required("Please give a name to this app")
		.max(50, "Please use 50 or fewer characters for the name"),
	description: Validator.string()
		.required("Please describe what this app is for")
		.max(500, "Please use 500 or fewer characters for the description"),
	oauth: Validator.object().shape({
		client_type: Validator.string().required("Please select the client"),
		allowed_scopes: Validator.array()
			.of(Validator.string())
			.min(1, "Allow at least one permission"),
		redirect_uris: Validator.array().when("client_type", {
			is: CUSTOM_CLIENT_TYPE,
			then: (schema) =>
				schema
					.of(redirectUriValidator)
					.min(1, "Add at least one redirect URI")
					.max(5, "Up to 5 redirect URIs are allowed"),
			otherwise: (schema) => schema.notRequired(),
		}),
	}),
})

export function NewOAuthIntegration({
	onSuccess,
	onCancel,
}: {
	onSuccess: (data: {
		integration: TIntegration
		oauthClient?: TOAuthClientCredentials
	}) => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	return (
		<NewOAuthIntegrationForm
			onCancel={onCancel}
			onSubmit={async (values) => {
				const { data: integration, meta } = await XHR(xhr).store(values)
				onSuccess({ integration, oauthClient: meta?.oauth_client })
			}}
		/>
	)
}

export function NewOAuthIntegrationForm({
	onSubmit,
	onCancel,
}: {
	onSubmit: (data: TNewOAuthIntegrationSchema) => Promise<unknown>
	onCancel: () => void
}) {
	const { data: options, error } = useFetchOAuthIntegrationOptions()
	const [initialValues] = useState<TNewOAuthIntegrationSchema>(() => ({
		name: "",
		description: "",
		abilities: [],
		platform: "oauth",
		oauth: {
			client_type: "",
			redirect_uris: [],
			allowed_scopes: DEFAULT_ALLOWED_SCOPES,
		},
	}))
	if (!options) {
		if (error) {
			return (
				<Alert status="error">
					{error.message || "Something went wrong. Please try after sometime."}
				</Alert>
			)
		}
		return <Spinner alignCenter padding="4" />
	}
	return (
		<Form<TNewOAuthIntegrationSchema>
			initialValues={initialValues}
			validate={validateFormValues(newOAuthIntegrationValidator)}
			onSubmit={withServerErrors(async (values) => {
				await onSubmit({
					...values,
					oauth: {
						...values.oauth,
						redirect_uris:
							values.oauth.client_type === CUSTOM_CLIENT_TYPE
								? values.oauth.redirect_uris
								: [],
					},
				})
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="6">
						<Grid gap="6">
							<Col sm={12} md={5}>
								<Stack gap="2">
									<Inline gap="4">
										<Box>
											<PlatformIcon platform="oauth" size="40" />
										</Box>
										<Heading>Client</Heading>
									</Inline>
									<Text color="muted">
										The application that will connect to Sembark on behalf of
										your team members. Each member approves it once with their
										own Sembark login; nothing of your Sembark password is
										shared with the client.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="3">
									{options.client_types.map((clientType) => (
										<RadioInputField
											key={clientType.id}
											name="oauth.client_type"
											value={clientType.id}
											label={clientType.name}
											expandLabel
											help={
												clientType.redirect_uris.length ? (
													<Text fontSize="xs" color="muted">
														Redirects to {clientType.redirect_uris.join(", ")}
													</Text>
												) : (
													<Text fontSize="xs" color="muted">
														Any OAuth 2.1 client with its own redirect URIs
													</Text>
												)
											}
										/>
									))}
									<GetFieldValue<TOAuthClientType> name="oauth.client_type">
										{({ value: clientType }) =>
											clientType === CUSTOM_CLIENT_TYPE ? (
												<RedirectUrisFieldArray name="oauth.redirect_uris" />
											) : null
										}
									</GetFieldValue>
								</Stack>
							</Col>
						</Grid>
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={5}>
								<Stack gap="2">
									<Heading>Permissions</Heading>
									<Text color="muted">
										What the client may read through Sembark. Members only ever
										see the trips they already have access to; these permissions
										cannot widen that.
									</Text>
								</Stack>
							</Col>
							<Col>
								<ScopeCheckboxes
									name="oauth.allowed_scopes"
									scopes={options.scopes}
								/>
							</Col>
						</Grid>
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={5}>
								<Stack gap="2">
									<Heading>App Details</Heading>
									<Text color="muted">
										A name and description so the team recognises this app in
										the integrations list and on the approval screen.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										label="Name"
										name="name"
										type="text"
										required
										autoComplete="false"
										placeholder="e.g. Sales Team Assistant"
									/>
									<TextInputField
										label="Description"
										name="description"
										type="text"
										required
										autoComplete="false"
										placeholder="e.g. Lets the sales team look up trips and quotes from their assistant."
									/>
								</Stack>
							</Col>
						</Grid>
						<SubmissionError />
						<Divider sm />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Please wait..." : "Create App"}
							</Button>
							<Button disabled={submitting} onClick={onCancel}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function ScopeCheckboxes({
	name,
	scopes,
}: {
	name: string
	scopes: Array<TOAuthScope>
}) {
	return (
		<Stack gap="3">
			{scopes.map((scope) => (
				<CheckboxInputField
					key={scope.id}
					name={name}
					value={scope.id}
					label={scope.description}
					secondaryLabel={scope.id}
				/>
			))}
		</Stack>
	)
}

function RedirectUrisFieldArray({ name }: { name: string }) {
	return (
		<FieldArray<string> name={name}>
			{({ fields }) => (
				<Stack gap="2">
					<Text fontWeight="semibold">Redirect URIs</Text>
					<Text fontSize="sm" color="muted">
						Where the client receives the authorization code. Must use https; up
						to 5.
					</Text>
					{fields.map((fieldName, index) => (
						<Inline key={fieldName} gap="2" alignItems="start">
							<Box flex="1">
								<TextInputField
									name={fieldName}
									type="url"
									placeholder="https://example.com/oauth/callback"
								/>
							</Box>
							<Button
								size="sm"
								level="tertiary"
								status="danger"
								onClick={() => fields.remove(index)}
								title="Remove"
							>
								<Icons.Trash />
							</Button>
						</Inline>
					))}
					{(fields.length || 0) < 5 ? (
						<Box>
							<Button size="sm" onClick={() => fields.push("")}>
								<Icons.Plus /> Add Redirect URI
							</Button>
						</Box>
					) : null}
				</Stack>
			)}
		</FieldArray>
	)
}

/**
 * The client id and secret, shown the one time the API returns the secret in
 * clear text: right after creating the app or rotating the secret.
 */
export function OAuthClientCredentialsAlert({
	credentials,
	title = "App created. Copy the client secret now.",
}: {
	credentials: TOAuthClientCredentials
	title?: string
}) {
	return (
		<Alert status="success" title={title}>
			<Stack gap="3">
				<Text>
					The secret is shown only once. Store it in the client's settings now;
					if it is lost, regenerate a new one from this page.
				</Text>
				<Grid gap="4">
					<Col sm={12} md={6}>
						<CopyValue label="Client ID" value={credentials.client_id} />
					</Col>
					<Col sm={12} md={6}>
						<CopyValue
							label="Client Secret"
							value={credentials.client_secret}
						/>
					</Col>
				</Grid>
			</Stack>
		</Alert>
	)
}

/**
 * The OAuth app's page: a header with the app's identity and actions, then
 * tabs routed under `/integrations/:integrationId/oauth/*` (setup,
 * permissions, members, requests), rendered through `children` by the
 * route layout.
 */
export function OAuthIntegratedApp({
	integration,
	createdCredentials,
	children,
}: {
	integration: TOAuthIntegration
	/** From the create flow, when the page is reached right after creation. */
	createdCredentials?: TOAuthClientCredentials
	children?: (props: { integration: TOAuthIntegration }) => ReactNode
}) {
	const xhr = useXHR()
	const app = integration.oauth_app
	const [credentials, setCredentials] = useState<
		{ value: TOAuthClientCredentials; title: string } | undefined
	>(
		createdCredentials
			? {
					value: createdCredentials,
					title: "App created. Copy the client secret now.",
				}
			: undefined
	)
	const [regenerating, setRegenerating] = useState(false)
	const connectedCount = app.connected_users.length
	const tabs: Array<{ to: string; label: string; icon: ReactNode }> = [
		{
			to: generatePath("/integrations/:integrationId/oauth/setup", {
				integrationId: integration.uid,
			}),
			label: "Setup",
			icon: <Icons.Cog />,
		},
		{
			to: generatePath("/integrations/:integrationId/oauth/permissions", {
				integrationId: integration.uid,
			}),
			label: "Permissions",
			icon: <Icons.ShieldCheck />,
		},
		{
			to: generatePath("/integrations/:integrationId/oauth/members", {
				integrationId: integration.uid,
			}),
			label: "Members",
			icon: <Icons.UserGroup />,
		},
		{
			to: generatePath("/integrations/:integrationId/oauth/requests", {
				integrationId: integration.uid,
			}),
			label: "Requests",
			icon: <Icons.DocumentText />,
		},
	]
	return (
		<Stack gap="4">
			<Box borderWidth="1" rounded="md" padding="4">
				<Inline gap="4" collapseBelow="sm" justifyContent="between">
					<Inline gap="4">
						<Box>
							<PlatformIcon
								platform="oauth"
								clientType={app.client_type}
								size="40"
							/>
						</Box>
						<Stack gap="2">
							<Stack gap="1">
								<Heading fontSize="lg">{integration.name}</Heading>
								<Text color="muted">{integration.description}</Text>
							</Stack>
							<Inline gap="2" alignItems="center" flexWrap="wrap">
								<Badge>{app.client_type_name}</Badge>
								{integration.paused_at ? (
									<Badge warning>
										<Icons.Ban /> Paused
									</Badge>
								) : connectedCount ? (
									<Badge success>
										<Icons.OkCircle /> {connectedCount}{" "}
										{connectedCount === 1 ? "member" : "members"} connected
									</Badge>
								) : (
									<Badge>No members connected</Badge>
								)}
								<Text fontSize="sm" color="muted">
									Client ID{" "}
									<Text as="span" fontFamily="mono">
										{app.client_id}
									</Text>
								</Text>
							</Inline>
						</Stack>
					</Inline>
					<Box>
						<Dropdown alignRight>
							<Dropdown.ToggleButton level="tertiary">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<Dropdown.MenuItem
									color="warning"
									onClick={async () => {
										if (
											regenerating ||
											!window.confirm(
												`The current client secret stops working at once; every client configured with it must be updated with the new one. Members who already approved this app keep their access.\n\nRegenerate the client secret?`
											)
										) {
											return
										}
										setRegenerating(true)
										try {
											const value = await XHR(xhr).regenerateOAuthSecret(
												integration.id
											)
											setCredentials({
												value,
												title: "New client secret. Copy it now.",
											})
											showSnackbar("Client secret regenerated")
										} catch (e) {
											const error = e as Error
											alert(
												error.message ||
													"Something went wrong. Please try after sometime."
											)
										} finally {
											setRegenerating(false)
										}
									}}
								>
									<Icons.Refresh spin={regenerating} /> Regenerate Secret
								</Dropdown.MenuItem>
							</Dropdown.Menu>
						</Dropdown>
					</Box>
				</Inline>
			</Box>
			{credentials ? (
				<OAuthClientCredentialsAlert
					credentials={credentials.value}
					title={credentials.title}
				/>
			) : null}
			<Box borderWidth="1" rounded="md" overflow="hidden">
				<Tabs layout="col">
					<TabsList>
						{tabs.map(({ to, label, icon }) => (
							<NavLink key={to} to={to}>
								{icon} {label}
							</NavLink>
						))}
					</TabsList>
					<TabContent>
						<Box>{children?.({ integration })}</Box>
					</TabContent>
				</Tabs>
			</Box>
		</Stack>
	)
}

/**
 * Setup tab: what an admin pastes into the client, and how, per client type.
 */
export function OAuthAppSetup({
	integration,
}: {
	integration: TOAuthIntegration
}) {
	const app = integration.oauth_app
	return (
		<Stack padding="4" gap="6">
			<Grid gap="6">
				<Col sm={12} md={4}>
					<Stack gap="1">
						<Heading as="h3" fontSize="md">
							OAuth Client
						</Heading>
						<Text color="muted">
							How the client authenticates with Sembark. The secret was shown
							when the app was created; regenerate it from the menu above if it
							was not saved.
						</Text>
					</Stack>
				</Col>
				<Col>
					<Stack gap="4">
						<CopyValue label="Client ID" value={app.client_id} />
						<Grid gap="4">
							<Col sm={12} md={6}>
								<CopyValue
									label="Authorization URL"
									value={app.endpoints.authorization_url}
								/>
							</Col>
							<Col sm={12} md={6}>
								<CopyValue label="Token URL" value={app.endpoints.token_url} />
							</Col>
						</Grid>
						<CopyValue
							label="Authorization Server Metadata"
							value={app.endpoints.authorization_server_metadata_url}
						/>
					</Stack>
				</Col>
			</Grid>
			<Divider marginY="0" />
			<Grid gap="6">
				<Col sm={12} md={4}>
					<Stack gap="1">
						<Heading as="h3" fontSize="md">
							Resources
						</Heading>
						<Text color="muted">
							What the client can reach with this app's tokens. The permissions
							decide what it sees there.
						</Text>
					</Stack>
				</Col>
				<Col>
					<Stack gap="2">
						<CopyValue label="MCP Server URL" value={app.endpoints.mcp_url} />
						<Text fontSize="sm" color="muted">
							For AI assistants and other MCP clients: tools to search and read
							trips, as the connected member.
						</Text>
					</Stack>
				</Col>
			</Grid>
			<Divider marginY="0" />
			<Grid gap="6">
				<Col sm={12} md={4}>
					<Stack gap="1">
						<Heading as="h3" fontSize="md">
							In the Client
						</Heading>
						<Text color="muted">
							Where the values above go, for {app.client_type_name}.
						</Text>
					</Stack>
				</Col>
				<Col>
					<ClientSetupInstructions clientType={app.client_type} />
				</Col>
			</Grid>
		</Stack>
	)
}

/**
 * Permissions tab: the scopes the client may ask for and, on a custom
 * client, where it may be sent back to. Saving refreshes the integration
 * the page is built from.
 */
export function OAuthAppPermissions({
	integration,
}: {
	integration: TOAuthIntegration
}) {
	const app = integration.oauth_app
	const { mutate } = useFetchIntegration(integration.uid)
	const [editingScopes, setEditingScopes] = useState(false)
	const [editingRedirectUris, setEditingRedirectUris] = useState(false)
	const isCustom = app.client_type === CUSTOM_CLIENT_TYPE
	return (
		<Stack padding="4" gap="6">
			<Grid gap="6">
				<Col sm={12} md={4}>
					<Stack gap="1">
						<Heading as="h3" fontSize="md">
							Permissions
						</Heading>
						<Text color="muted">
							What the client may ask for. Members already connected keep what
							they approved; the change applies from their next approval.
						</Text>
					</Stack>
				</Col>
				<Col>
					{editingScopes ? (
						<EditAllowedScopesForm
							integration={integration}
							onCancel={() => setEditingScopes(false)}
							onSuccess={() => {
								setEditingScopes(false)
								mutate()
							}}
						/>
					) : (
						<Stack gap="3">
							<Stack as="ul" gap="1">
								{app.allowed_scopes.map((scope) => (
									<Inline as="li" key={scope.id} gap="2" alignItems="center">
										<Icons.ShieldCheck color="success" />
										<Text>{scope.description}</Text>
										<Text fontSize="xs" color="muted">
											{scope.id}
										</Text>
									</Inline>
								))}
							</Stack>
							<Box>
								<Button size="sm" onClick={() => setEditingScopes(true)}>
									<Icons.Pencil /> Change Permissions
								</Button>
							</Box>
						</Stack>
					)}
				</Col>
			</Grid>
			<Divider marginY="0" />
			<Grid gap="6">
				<Col sm={12} md={4}>
					<Stack gap="1">
						<Heading as="h3" fontSize="md">
							Redirect URIs
						</Heading>
						<Text color="muted">
							{isCustom
								? "Where the client receives the authorization code."
								: `Fixed for ${app.client_type_name}; published by the vendor.`}
						</Text>
					</Stack>
				</Col>
				<Col>
					{editingRedirectUris ? (
						<EditRedirectUrisForm
							integration={integration}
							onCancel={() => setEditingRedirectUris(false)}
							onSuccess={() => {
								setEditingRedirectUris(false)
								mutate()
							}}
						/>
					) : (
						<Stack gap="3">
							<Stack gap="2">
								{app.redirect_uris.map((uri) => (
									<CopyValue key={uri} value={uri} />
								))}
							</Stack>
							{isCustom ? (
								<Box>
									<Button
										size="sm"
										onClick={() => setEditingRedirectUris(true)}
									>
										<Icons.Pencil /> Change Redirect URIs
									</Button>
								</Box>
							) : null}
						</Stack>
					)}
				</Col>
			</Grid>
		</Stack>
	)
}

/**
 * Members tab: who holds a live token for this app, and how they use it.
 */
export function OAuthAppMembers({
	integration,
}: {
	integration: TOAuthIntegration
}) {
	const app = integration.oauth_app
	const { mutate } = useFetchIntegration(integration.uid)
	return (
		<Stack padding="4" gap="3">
			{!app.connected_users.length ? (
				<Box padding="6" textAlign="center">
					<Text>
						No member holds a live token for this app. A member appears here
						after approving the app from the client, and leaves when their
						access runs out, they disconnect it from their security settings, or
						they are disconnected here.
					</Text>
				</Box>
			) : (
				<Table
					headers={[
						"Member",
						"Permissions",
						"Connected",
						"Access until",
						"Last used",
						"Requests (30 days)",
						"",
					]}
					alignCols={{ 5: "right", 6: "right" }}
					responsive
					bordered
					hover
					rows={app.connected_users.map(
						({
							user,
							scopes,
							authorized_at,
							expires_at,
							last_used_at,
							requests_last_30d,
						}) => [
							<Stack key={user.id}>
								<Text fontWeight="semibold">{user.name}</Text>
								<Text fontSize="sm" color="muted">
									{user.email}
								</Text>
							</Stack>,
							<Inline key="scopes" gap="1" flexWrap="wrap">
								{scopes.map((scope) => (
									<Badge key={scope}>{scope}</Badge>
								))}
							</Inline>,
							authorized_at ? (
								<RelativeTime key="authorized" timestamp={authorized_at} />
							) : (
								"-"
							),
							expires_at ? <Time key="expires" timestamp={expires_at} /> : "-",
							last_used_at ? (
								<RelativeTime key="used" timestamp={last_used_at} />
							) : (
								<Text key="used" color="muted">
									Never
								</Text>
							),
							requests_last_30d,
							<DisconnectOAuthMemberButton
								key="disconnect"
								integrationId={integration.id}
								userId={user.id}
								userName={user.name}
								onDisconnected={() => mutate()}
							/>,
						]
					)}
				/>
			)}
			{integration.paused_at ? (
				<Text fontSize="sm" color="muted">
					The integration is paused: every request from this app is refused
					until it is resumed. Connected members keep their approval.
				</Text>
			) : null}
		</Stack>
	)
}

/**
 * The signed-in member's own approvals, on their security settings next to
 * the browser sessions: what each app may do, and a way to cut it off.
 */
export function ConnectedOAuthAppsList() {
	const xhr = useXHR()
	const { data, mutate } = useFetchConnectedOAuthApps()
	const [disconnecting, setDisconnecting] = useState<string | undefined>()
	if (!data) return <Spinner padding="4" alignCenter />
	if (!data.length) {
		return (
			<Text color="muted">
				You have not connected any app. When you approve an app such as Claude
				or ChatGPT to work with Sembark for you, it appears here.
			</Text>
		)
	}
	return (
		<Table
			headers={["App", "Permissions", "Connected", "Last used", ""]}
			alignCols={{ 4: "right" }}
			bordered
			responsive
			rows={data.map((app) => [
				<Inline key="app" gap="2" alignItems="center">
					<PlatformIcon platform="oauth" clientType={app.client_type} />
					<Stack>
						<Text fontWeight="semibold">{app.name}</Text>
						<Text fontSize="sm" color="muted">
							{app.client_type_name}
							{app.is_paused ? " · Paused by your admin" : ""}
						</Text>
					</Stack>
				</Inline>,
				<Stack key="scopes" gap="1">
					{app.scopes.map((scope) => (
						<Text key={scope.id} fontSize="sm">
							{scope.description}
						</Text>
					))}
				</Stack>,
				app.authorized_at ? (
					<RelativeTime key="authorized" timestamp={app.authorized_at} />
				) : (
					"-"
				),
				app.last_used_at ? (
					<RelativeTime key="used" timestamp={app.last_used_at} />
				) : (
					<Text key="used" color="muted">
						Never
					</Text>
				),
				<Button
					key="disconnect"
					size="sm"
					level="tertiary"
					status="danger"
					disabled={disconnecting === app.uid}
					onClick={async () => {
						if (
							!window.confirm(
								`${app.name} loses access to your Sembark account on its next request. You can approve it again from ${app.client_type_name}.\n\nDisconnect ${app.name}?`
							)
						) {
							return
						}
						setDisconnecting(app.uid)
						try {
							await XHR(xhr).disconnectConnectedOAuthApp(app.uid)
							showSnackbar(`${app.name} disconnected`)
							await mutate()
						} catch (e) {
							const error = e as Error
							alert(
								error.message ||
									"Something went wrong. Please try after sometime."
							)
						} finally {
							setDisconnecting(undefined)
						}
					}}
				>
					{disconnecting === app.uid ? "Disconnecting..." : "Disconnect"}
				</Button>,
			])}
		/>
	)
}

function DisconnectOAuthMemberButton({
	integrationId,
	userId,
	userName,
	onDisconnected,
}: {
	integrationId: number
	userId: number
	userName: string
	onDisconnected: () => void
}) {
	const xhr = useXHR()
	const [disconnecting, setDisconnecting] = useState(false)
	return (
		<Button
			size="sm"
			level="tertiary"
			status="danger"
			disabled={disconnecting}
			onClick={async () => {
				if (
					!window.confirm(
						`${userName} loses access through this app on its next request. They can approve it again from the client.\n\nDisconnect ${userName}?`
					)
				) {
					return
				}
				setDisconnecting(true)
				try {
					await XHR(xhr).disconnectOAuthMember(integrationId, userId)
					showSnackbar(`${userName} disconnected`)
					onDisconnected()
				} catch (e) {
					const error = e as Error
					alert(
						error.message || "Something went wrong. Please try after sometime."
					)
				} finally {
					setDisconnecting(false)
				}
			}}
		>
			{disconnecting ? "Disconnecting..." : "Disconnect"}
		</Button>
	)
}

type TOutcomeOption = { id: TOAuthAccessLogOutcome; name: string }

const OUTCOME_OPTIONS: Array<TOutcomeOption> = [
	{ id: "ok", name: "Succeeded" },
	{ id: "error", name: "Failed in the tool" },
	{ id: "denied", name: "Denied" },
	{ id: "invalid", name: "Invalid request" },
]

type TRequestFilters = TSearchParams & {
	user?: IUser
	outcome?: TOutcomeOption
	from?: Date | string
	to?: Date | string
}

/**
 * Requests tab: what the app did on members' behalf, from the OAuth access
 * log. One row per request, newest first, kept for 90 days.
 */
export function OAuthAppRequests({
	integration,
}: {
	integration: TOAuthIntegration
}) {
	const [params, setParams] = useSearch<TRequestFilters>({ q: "" })
	return (
		<Search<TRequestFilters>
			title="Requests"
			placeholder="Search by operation, e.g. search_trips"
			initialParams={params}
			Filters={RequestFilters}
			onSearch={(next) => setParams({ ...next, page: 1 })}
			resetParams={{ q: "", page: 1 }}
			areAdvancedFiltersApplied={(filters) =>
				areAdvancedFiltersAppliedDefault(filters)
			}
		>
			<ListView<TOAuthAccessLog, TRequestFilters>
				pageKey={`integrations/${integration.id}/oauth/requests`}
				params={params}
				fetch={(xhr, { q, user, outcome, from, to, page, limit }) =>
					XHR(xhr).getOAuthRequests(integration.id, {
						page,
						limit,
						operation: q || undefined,
						user: user?.id,
						outcome: outcome?.id,
						from: from ? dateToUTCString(startOf(from, "day")) : undefined,
						to: to ? dateToUTCString(endOf(to, "day")) : undefined,
					})
				}
				onPageChange={(page) => setParams({ ...params, page })}
				emptyState={({ searched, defaultValue }) =>
					searched ? (
						defaultValue
					) : (
						<Box padding="6" textAlign="center">
							<Text>
								No requests yet. Every call the app makes on a member's behalf
								is listed here for 90 days.
							</Text>
						</Box>
					)
				}
			>
				{({ items }) => <RequestsTable items={items} />}
			</ListView>
		</Search>
	)
}

function RequestFilters() {
	return (
		<Stack gap="4">
			<SelectField
				label="Member"
				name="user"
				select={SelectUsers}
				multiple={false}
				fetchOnMount
				placeholder="Any member"
			/>
			<SelectField
				select={Select}
				label="Outcome"
				name="outcome"
				options={OUTCOME_OPTIONS}
				placeholder="Any outcome"
			/>
			<DateRangePickerField label="Between" fromName="from" toName="to" />
		</Stack>
	)
}

function RequestsTable({ items }: { items: Array<TOAuthAccessLog> }) {
	return (
		<Table
			headers={["When", "Member", "Request", "Arguments", "Outcome", "Took"]}
			alignCols={{ 5: "right" }}
			responsive
			bordered
			hover
			rows={items.map((log) => [
				<Box key="when" whiteSpace="nowrap">
					<Time timestamp={log.created_at} dateFormat timeFormat />
				</Box>,
				log.user ? (
					<Stack key="user">
						<Text>{log.user.name}</Text>
						<Text fontSize="sm" color="muted">
							{log.user.email}
						</Text>
					</Stack>
				) : (
					<Text key="user" color="muted">
						Unknown
					</Text>
				),
				<Stack key="request">
					<Text fontWeight="semibold">
						{log.operation || log.method || "-"}
					</Text>
					{log.operation && log.method ? (
						<Text fontSize="sm" color="muted">
							{log.method}
						</Text>
					) : null}
				</Stack>,
				<RequestArguments
					key="arguments"
					args={log.arguments}
					resourceIds={log.resource_ids}
				/>,
				<Stack key="outcome" gap="1">
					<Box>
						<OutcomeBadge outcome={log.outcome} status={log.status} />
					</Box>
					{log.error ? (
						<Text fontSize="sm" color="muted">
							{log.error}
						</Text>
					) : null}
				</Stack>,
				<Text key="took" whiteSpace="nowrap">
					{log.duration_ms !== null ? `${log.duration_ms} ms` : "-"}
				</Text>,
			])}
		/>
	)
}

function OutcomeBadge({
	outcome,
	status,
}: {
	outcome: TOAuthAccessLogOutcome
	status: number
}) {
	const label = OUTCOME_OPTIONS.find((o) => o.id === outcome)?.name || outcome
	// MCP reports tool and JSON-RPC errors inside a 200; the HTTP status only
	// says something when the transport or auth layer answered instead
	const text = status === 200 ? label : `${label} · ${status}`
	switch (outcome) {
		case "ok":
			return <Badge success>{text}</Badge>
		case "denied":
			return <Badge danger>{text}</Badge>
		default:
			return <Badge warning>{text}</Badge>
	}
}

function RequestArguments({
	args,
	resourceIds,
}: {
	args: TOAuthAccessLog["arguments"]
	resourceIds: TOAuthAccessLog["resource_ids"]
}) {
	const argEntries = Object.entries(args || {})
	const idEntries = Object.entries(resourceIds || {})
	if (!argEntries.length && !idEntries.length) {
		return <Text color="muted">-</Text>
	}
	return (
		<Stack gap="1" fontSize="sm">
			{argEntries.map(([key, value]) => (
				<Text key={key} fontFamily="mono">
					{key}: {typeof value === "string" ? value : JSON.stringify(value)}
				</Text>
			))}
			{idEntries.map(([type, ids]) => (
				<Text key={type} color="muted">
					{type}: {ids.join(", ")}
				</Text>
			))}
		</Stack>
	)
}

function ClientSetupInstructions({
	clientType,
}: {
	clientType: TOAuthClientType
}) {
	switch (clientType) {
		case "claude":
			return (
				<Alert status="info" title="Setting up in Claude">
					<Stack as="ol" listStyleType="decimal" paddingLeft="4" gap="1">
						<Box as="li">
							In Claude, open <b>Settings &gt; Connectors</b> and choose{" "}
							<b>Add custom connector</b>.
						</Box>
						<Box as="li">
							Paste the <b>MCP Server URL</b> from Resources as the connector
							URL.
						</Box>
						<Box as="li">
							Under advanced settings, paste the <b>Client ID</b> and the{" "}
							<b>Client Secret</b>.
						</Box>
						<Box as="li">
							Connect: Claude opens Sembark, the member signs in and approves
							the permissions of this app.
						</Box>
					</Stack>
				</Alert>
			)
		case "chatgpt":
			return (
				<Alert status="info" title="Setting up in ChatGPT">
					<Stack as="ol" listStyleType="decimal" paddingLeft="4" gap="1">
						<Box as="li">
							In ChatGPT, open <b>Settings &gt; Connectors</b> and create a new
							connector.
						</Box>
						<Box as="li">
							Paste the <b>MCP Server URL</b> from Resources, choose OAuth, and
							paste the <b>Client ID</b> and <b>Client Secret</b>.
						</Box>
						<Box as="li">
							Connect: ChatGPT opens Sembark, the member signs in and approves
							the permissions of this app.
						</Box>
					</Stack>
				</Alert>
			)
		default:
			return (
				<Alert status="info" title="Setting up a custom client">
					<Text>
						Configure the client with the authorization and token URLs (or let
						it discover them from the authorization server metadata), the client
						id and secret, the authorization code grant with PKCE, and the
						permission ids as scopes. Then point it at the resource it needs,
						such as the MCP Server URL.
					</Text>
				</Alert>
			)
	}
}

function EditAllowedScopesForm({
	integration,
	onCancel,
	onSuccess,
}: {
	integration: TOAuthIntegration
	onCancel: () => void
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const { data: options, error } = useFetchOAuthIntegrationOptions()
	const [initialValues] = useState(() => ({
		allowed_scopes: integration.oauth_app.allowed_scopes.map((s) => s.id),
	}))
	if (!options) {
		if (error) {
			return (
				<Alert status="error">
					{error.message || "Something went wrong. Please try after sometime."}
				</Alert>
			)
		}
		return <Spinner alignCenter padding="4" />
	}
	return (
		<Form<{ allowed_scopes: Array<string> }>
			initialValues={initialValues}
			validate={validateFormValues(
				Validator.object().shape({
					allowed_scopes: Validator.array()
						.of(Validator.string())
						.min(1, "Allow at least one permission"),
				})
			)}
			onSubmit={withServerErrors(async (values) => {
				await XHR(xhr).updateOAuthApp(integration.id, {
					allowed_scopes: values.allowed_scopes,
				})
				showSnackbar("Permissions updated")
				onSuccess()
			})}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<ScopeCheckboxes name="allowed_scopes" scopes={options.scopes} />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" size="sm" disabled={submitting}>
								{submitting ? "Saving..." : "Save Permissions"}
							</Button>
							<Button size="sm" disabled={submitting} onClick={onCancel}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function EditRedirectUrisForm({
	integration,
	onCancel,
	onSuccess,
}: {
	integration: TOAuthIntegration
	onCancel: () => void
	onSuccess: () => void
}) {
	const xhr = useXHR()
	const [initialValues] = useState(() => ({
		redirect_uris: integration.oauth_app.redirect_uris,
	}))
	return (
		<Form<{ redirect_uris: Array<string> }>
			initialValues={initialValues}
			validate={validateFormValues(
				Validator.object().shape({
					redirect_uris: Validator.array()
						.of(redirectUriValidator)
						.min(1, "Add at least one redirect URI")
						.max(5, "Up to 5 redirect URIs are allowed"),
				})
			)}
			onSubmit={withServerErrors(async (values) => {
				await XHR(xhr).updateOAuthApp(integration.id, {
					redirect_uris: values.redirect_uris,
				})
				showSnackbar("Redirect URIs updated")
				onSuccess()
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<RedirectUrisFieldArray name="redirect_uris" />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" size="sm" disabled={submitting}>
								{submitting ? "Saving..." : "Save Redirect URIs"}
							</Button>
							<Button size="sm" disabled={submitting} onClick={onCancel}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function CopyValue({ label, value }: { label?: string; value: string }) {
	return (
		<CopyToClipboard>
			{({ copyNodeTextToClipboard, nodeToCopy }) => (
				<Stack gap="1">
					{label ? <Text fontWeight="semibold">{label}</Text> : null}
					<Inline
						maxWidth="full"
						bgColor="subtle"
						borderWidth="1"
						rounded="md"
						overflow="hidden"
						alignItems="center"
					>
						<Box
							style={{ maxWidth: "100%", overflow: "auto" }}
							paddingY="2"
							paddingX="4"
							flex="1"
						>
							<Text ref={nodeToCopy as never} whiteSpace="preserve">
								{value}
							</Text>
						</Box>
						<Box paddingX="2">
							<CopyToClipboardButton
								onClick={copyNodeTextToClipboard}
								size="sm"
								iconOnly
							>
								<Icons.ClipboardCopy />
							</CopyToClipboardButton>
						</Box>
					</Inline>
				</Stack>
			)}
		</CopyToClipboard>
	)
}

export type { TOAuthIntegrationOptions }
